import type { FormEvent } from 'react';
import getConfig from 'next/config';
import { safeFormatClientDate } from 'shared/util/datetime-fns';

import type { AutocompleteDestination, MapBounds, RoomWithPeople } from '@/interfaces';
import { coordinateToQueryParam, roomsToQueryParams } from '@/utils/url/queryParamConverters';

const { publicRuntimeConfig } = getConfig();

const basePath = publicRuntimeConfig.basePath;

const submitUrl = (destination?: AutocompleteDestination): string => {
    if (destination?.themePath) {
        return `${basePath}/${destination.themePath}/`;
    }
    if (destination?.type === 'hotel') {
        return `${basePath}/detaljer/${destination.key}/`;
    }
    return `${basePath}/resultat/${destination?.key}/${isNumericString(destination?.key) ? fakeNamedDestinationPathFromText(destination.text) : ''}`;
};

const isNumericString = (value: string): boolean => {
    return /^\d+$/.test(value);
};

// this is to show a destination name in the url, but it is not actually used for anything...
const fakeNamedDestinationPathFromText = (text: string): string => {
    const destination = text
        .split(',')[0]
        .toLowerCase()
        .replace(/æ/g, 'ae')
        .replace(/ø/g, 'o')
        .replace(/å/g, 'a')
        .replace(/[^\w\-]+/g, '_');
    return `${destination}/`;
};

export interface SearchFormSubmitProps {
    destination?: AutocompleteDestination;
    checkInDate?: Date | number;
    checkOutDate?: Date | number;
    rooms: readonly RoomWithPeople[];
    mapBounds?: MapBounds;
    mapOpen?: boolean;
    handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
    className?: string;
}

export const SearchFormSubmit = ({
    destination,
    checkInDate,
    checkOutDate,
    rooms,
    mapBounds,
    mapOpen,
    handleSubmit,
    className = '',
}: SearchFormSubmitProps) => (
    <div className={`u-display-inline-block u-size1of1 ${className}`}>
        <form onSubmit={handleSubmit} action={submitUrl(destination)} data-testid="searchForm" method="GET">
            {destination?.searchable && (
                <>
                    <input data-testid="checkInDate" type="hidden" name="checkInDate" value={safeFormatClientDate(checkInDate)} />
                    <input data-testid="checkOutDate" type="hidden" name="checkOutDate" value={safeFormatClientDate(checkOutDate)} />
                    {(roomsToQueryParams(rooms) || []).map((room, index) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: This existed before biome was added
                        <input key={`room-${index}`} data-testid={`room-${index}`} type="hidden" name="rooms" value={room} />
                    ))}
                </>
            )}
            {destination?.searchable && destination.type !== 'hotel' && mapBounds && (
                <>
                    <input data-testid="lngSw" type="hidden" name="lngSw" value={coordinateToQueryParam(mapBounds.sw, 0)} />
                    <input data-testid="latSw" type="hidden" name="latSw" value={coordinateToQueryParam(mapBounds.sw, 1)} />
                    <input data-testid="lngNe" type="hidden" name="lngNe" value={coordinateToQueryParam(mapBounds.ne, 0)} />
                    <input data-testid="latNe" type="hidden" name="latNe" value={coordinateToQueryParam(mapBounds.ne, 1)} />
                </>
            )}
            {destination?.searchable && destination.type !== 'hotel' && mapOpen && (
                <input data-testid="mapOpen" type="hidden" name="mapOpen" value="true" />
            )}
            <input type="submit" className="button button--cta u-size1of1" data-testid="searchButton" value="Søk" />
        </form>
    </div>
);
